import React, { useEffect, useState, useRef } from "react";
import {
  X,
  User,
  ListTodo,
  MessageSquare,
  FileText,
  Plus,
  Edit2,
  Eye,
  Download,
  Trash2,
  Clock,
} from "lucide-react";
import Swal2 from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import AddTask from "./addTask";
import AddContact from "./addContact";
import AddFile from "./addFile";
import ShowNote from "./showNote";
import "./app.css";
import { secretKey } from "./data";
import Loading from "./loading.js";
import { attachCopyLogger } from "./logCopy";
import FileViewer from "./fileViewer";
import AdditionalLead from "./additionalLead";

const MySwal = withReactContent(Swal2);
const API = "https://apiftpsplit.ftpeople.pl";
const hdrs = { "Content-Type": "application/json", Authorization: secretKey };

const TABS = [
  { id: 0, label: "Dane kontaktu", icon: User },
  { id: 1, label: "Zadania", icon: ListTodo },
  { id: 2, label: "Kontakty", icon: MessageSquare },
  { id: 3, label: "Pliki", icon: FileText },
  { id: 4, label: "Historia", icon: Clock },
];

const LeadDetail = (props) => {
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState(0);
  const [logs, setLogs] = useState([]);

  const [viewerUrl, setViewerUrl] = useState(null);
  const [viewerName, setViewerName] = useState("");
  const [lead, setLead] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [files, setFiles] = useState([]);
  const [users, setUsers] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [showNoteB, setShowNoteB] = useState(false);
  const [leadDetail, setLeadDetail] = useState(null);

  /* modals */
  const [showTaskAdd, setShowTaskAdd] = useState(false);
  const [showTaskEdit, setShowTaskEdit] = useState(false);
  const [taskEdit, setTaskEdit] = useState(null);
  const [showContactAdd, setShowContactAdd] = useState(false);
  const [showContactEdit, setShowContactEdit] = useState(false);
  const [contactEdit, setContactEdit] = useState(null);
  const [showFileAdd, setShowFileAdd] = useState(false);
  const [showNote, setShowNote] = useState(false);
  const [noteMode, setNoteMode] = useState("note");
  const [showAdditionalLead, setShowAdditionalLead] = useState(false);
  const [showAdditionalLeadId, setShowAdditionalLeadId] = useState("");
  const [showFileAddOffer, setShowFileAddOffer] = useState(false);
  const [wasServiced, setWasServiced] = useState(false);

  /* ── Inline AddTask mid-flow ── */
  const [showInlineTask, setShowInlineTask] = useState(false);
  const pendingAfterTaskRef = useRef(null);

  const [showAdditional, setShowAdditional] = useState(false);
  const [pendingExtTarget, setPendingExtTarget] = useState(null);

  const isServiceDept = ["SKD", "WIBOR", "CHF", "SLUZEBNOSC"].includes(
    props.option || lead?.company
  );

  const oferta = files.find((f) => f.type === "oferta");
  const regularFiles = files.filter((f) => f.type !== "oferta");

  /* ══════════ FETCH ══════════ */

  const post = async (endpoint, body = {}) => {
    const res = await fetch(`${API}/${endpoint}`, {
      method: "POST",
      headers: hdrs,
      body: JSON.stringify(body),
    });
    return res.json();
  };

  const getData = async () => {
    setLoading(true);
    try {
      const id = props.lead.id;
      const [
        leadData,
        tasksData,
        contactsData,
        filesData,
        usersData,
        statusData,
        logsData,
      ] = await Promise.all([
        post("getSingleLeadData", { id }),
        post("getSingleLeadTasksData", { id }),
        post("getSingleLeadContactsData", { id }),
        post("getSingleLeadFilesData", { id }),
        post("getUsersListAll"),
        post("getStatuses"),
        post("getLeadLogs", { id }),
      ]);
      setLogs(logsData || []);
      setLead(leadData[0] || {});
      setTasks(tasksData || []);
      setContacts(contactsData || []);
      setFiles(filesData || []);
      setUsers(usersData || []);
      setStatuses(statusData || []);
    } catch (err) {
      console.error("LeadDetail getData:", err);
    }
    setLoading(false);
  };

  useEffect(() => {
    getData();
  }, []);

  useEffect(() => {
    return attachCopyLogger(props.user);
  }, []);

  /* ══════════ HELPERS ══════════ */

  const addContact = async (content) => {
    await fetch(`${API}/addContact`, {
      method: "POST",
      headers: hdrs,
      body: JSON.stringify({
        id: lead.id,
        content,
        user: props.user.id,
        userId: props.user.id,
        userName: props.user.name,
      }),
    }).catch(() => {});
  };

  /* ══════════ OBSŁUGA LEADA ══════════ */

  const handleCloseCard = async () => {
    if (
      wasServiced ||
      !isServiceDept ||
      lead.status.toString().toLowerCase().includes("zainteresowany")
    ) {
      props.close();
      return;
    }

    const { isConfirmed } = await MySwal.fire({
      title: "Zamykanie karty",
      text: "Czy obsługiwałeś ten kontakt?",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Tak",
      cancelButtonText: "Nie, zamknij",
    });

    if (!isConfirmed) {
      props.close();
      return;
    }
    await askClientAnswered();
  };

  const handleServiceLead = async () => {
    setWasServiced(true);
    await askClientAnswered();
  };

  const askClientAnswered = async () => {
    const { isConfirmed } = await MySwal.fire({
      title: "Obsługa kontaktu",
      text: "Czy klient odebrał?",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Tak, odebrał",
      cancelButtonText: "Nie odebrał",
      reverseButtons: true,
    });

    if (!isConfirmed) {
      await handleNotAnswered();
    } else {
      await handleAnswered();
    }
  };

  /* ── Następny dzień roboczy (pomija sob/niedz) ── */
  const nextBusinessDay = () => {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    while (d.getDay() === 0 || d.getDay() === 6) d.setDate(d.getDate() + 1);
    return d.toISOString().split("T")[0];
  };

  const handleNotAnswered = async () => {
    const nextBizDay = nextBusinessDay();

    // Zapisz kontakt — nieodebrane
    await addContact(
      "Nieodebrane połączenie — automatyczne zadanie dodane na kolejny dzień roboczy"
    );

    // Automatyczny zapis zadania bez okna
    await fetch(`${API}/addTask`, {
      method: "POST",
      headers: hdrs,
      body: JSON.stringify({
        id: lead.id,
        task: "Skontaktuj się z kontaktem",
        date: nextBizDay,
        note: "Automatyczne zadanie — nieodebrane połączenie",
        user: props.user.id,
        type: "zwykłe",
        hour: "",
        add: "auto",
        userId: props.user.id,
        userName: props.user.name,
      }),
    }).catch(() => {});

    // Wyślij maila "Po nieudanej próbie kontaktu"
    await fetch(`${API}/sendServiceMail`, {
      method: "POST",
      headers: hdrs,
      body: JSON.stringify({
        leadId: lead.id,
        type: "nieodebrane",
        company: lead.company,
      }),
    });

    // Zapisz lastTalkDate
    await fetchEditLead(
      lead.id,
      "lastTalkDate",
      new Date().toISOString().split("T")[0]
    );

    MySwal.fire({
      icon: "info",
      title: "Nie odebrał",
      text:
        lead.company === "CHF"
          ? `Dodano zadanie na ${nextBizDay}`
          : `Dodano zadanie na ${nextBizDay} i wysłano maila`,
    });
  };

  /* ── Inline AddTask — zwraca Promise, która resolves po zamknięciu okna ── */
  const handleAddManualTask = (prefill = {}) =>
    new Promise((resolve) => {
      pendingAfterTaskRef.current = resolve;
      // przekaż prefill przez ref żeby AddTask mógł go odczytać
      pendingAfterTaskRef.prefill = prefill;
      setShowInlineTask(true);
    });

  const handleInlineTaskClose = () => {
    setShowInlineTask(false);
    getData();
    // odblokowuje oczekujący flow
    if (pendingAfterTaskRef.current) {
      pendingAfterTaskRef.current();
      pendingAfterTaskRef.current = null;
    }
  };

  /* ── Fetchowanie i zapis freshLead ── */

  const fetchFreshLead = async (leadId) => {
    const res = await fetch(`${API}/getSingleLeadData`, {
      method: "POST",
      headers: hdrs,
      body: JSON.stringify({ id: leadId }),
    });
    const rows = await res.json();
    return rows?.[0] || null;
  };

  const parseAndValidate = (lead) => {
    try {
      const v = JSON.parse(lead.validation);
      if (!v.bank || !v.ammount || !v.creditDate || !v.region)
        return { valid: false, v: null };
      return { valid: true, v };
    } catch {
      return { valid: false, v: null };
    }
  };

  const doSendExternal = async (lead, target) => {
    const secret = "Ftp@)2%jsj@*";
    const { v } = parseAndValidate(lead);

    const body = {
      name: lead.name,
      phone: lead.phone,
      mail: lead.mail,
      creditAgreement: v.bank,
      creditAmmount: v.ammount,
      creditDate: v.creditDate,
      creditCurrency: "PLN",
      leadArea: v.region,
      leadExpectations: "Cena",
      ...(target === "PCHF" ? { noContact: "false" } : {}),
    };

    const url =
      target === "PCHF"
        ? "https://porownywarkachfapi.ftpeople.pl/insert_lead_basic"
        : "https://api.twojeskd.pl/insert_lead_basic";

    await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: secret },
      body: JSON.stringify(body),
    });

    MySwal.fire({ icon: "success", title: "Wysłano", text: `do ${target}` });
  };

  const sendExternal = async (lead, target) => {
    const { valid } = parseAndValidate(lead);

    if (valid) {
      try {
        await doSendExternal(lead, target);
      } catch {
        MySwal.fire({
          icon: "error",
          title: "Błąd",
          text: "Nie udało się wysłać.",
        });
      }
      return;
    }

    setPendingExtTarget(target);
    setShowAdditional(true);
  };

  const handleAdditionalClose = async () => {
    setShowAdditional(false);

    if (!pendingExtTarget) return;

    const target = pendingExtTarget;
    setPendingExtTarget(null);

    const fresh = await fetchFreshLead(lead.id).catch(() => null);
    if (!fresh) return;

    const { valid } = parseAndValidate(fresh);
    if (!valid) {
      MySwal.fire({
        icon: "warning",
        title: "Niepełne dane",
        text: "Wysyłka nieudana. Uzupełnij bank, kwotę, datę kredytu i region, uruchom ponownie obsługę pozostawiając status.",
        confirmButtonColor: "#ff8c00",
      });
      return;
    }

    try {
      await doSendExternal(fresh, target);
    } catch {
      MySwal.fire({
        icon: "error",
        title: "Błąd",
        text: "Nie udało się wysłać.",
      });
    }
  };

  const handleAnswered = async () => {
    const model = {};
    statuses.forEach((s) => {
      model[s.name] = s.name;
    });

    const { isConfirmed, value: newStatus } = await MySwal.fire({
      title: "Klient odebrał — zmień status",
      input: "select",
      inputOptions: model,
      inputValue: lead.status,
      inputPlaceholder: "wybierz status",
      showCancelButton: true,
      confirmButtonText: "Zapisz",
      cancelButtonText: "Anuluj",
      inputValidator: (v) => (!v ? "Musisz wybrać status" : undefined),
    });

    if (!isConfirmed) return;

    // Zapisz lastTalkDate
    await fetchEditLead(
      lead.id,
      "lastTalkDate",
      new Date().toISOString().split("T")[0]
    );

    const statusUnchanged = newStatus === lead.status;
    const isZrezygnowano = newStatus.toLowerCase() === "zrezygnowano";
    const isZainteresowany = newStatus.toLowerCase().includes("zainteresowany");
    const isPrzypisany = newStatus.toLowerCase().includes("przypisano");
    const isSKDAss = newStatus.toLowerCase().includes("przypisano tskd");
    const isCHFAss = newStatus.toLowerCase().includes("przypisano pchf");

    if (statusUnchanged && !isZrezygnowano && !isPrzypisany) {
      // Notatka najpierw (zapisuje też jako kontakt)
      await handleAddStatusNote();
      // Inline AddTask — tylko w tym przypadku
      await handleAddManualTask();
    } else if (isSKDAss) {
      await sendExternal(lead, "TSKD");
      await handleAddStatusNote();
      await fetchEditLead(lead.id, "status", newStatus);
    } else if (isCHFAss) {
      await sendExternal(lead, "PCHF");
      await handleAddStatusNote();
      await fetchEditLead(lead.id, "status", newStatus);
    } else if (isZainteresowany) {
      await fetchEditLead(lead.id, "status", newStatus);
      await handleSendThankYouMail();
      setShowAdditionalLead(true);
    } else {
      await handleAddStatusNote();
      await fetchEditLead(lead.id, "status", newStatus);
      if (isZrezygnowano || !isZainteresowany) {
        await fetchEditLead(lead.id, "seller", "31");
      }
      MySwal.fire({
        icon: "success",
        title: "Zapisano",
        text: `Status: ${newStatus}`,
      });
    }
  };

  /* ── Notatka statusowa — SweetAlert + zapis jako kontakt ── */
  const handleAddStatusNote = async () => {
    const { isConfirmed, value } = await MySwal.fire({
      title: "Notatka wewnętrzna",
      input: "textarea",
      inputPlaceholder: "Opisz przebieg rozmowy...",
      inputValue: lead.statusNote || "",
      showCancelButton: false,
      confirmButtonText: "Zapisz",
      allowOutsideClick: false,
      allowEscapeKey: false,
    });

    if (isConfirmed && value) {
      // Zapis do pola statusNote
      await fetchEditLead(lead.id, "statusNote", value);
      // Zapis jako kontakt (historia rozmów)
      await addContact(value);
    }
  };

  const handleSendThankYouMail = async () => {
    await fetch(`${API}/sendServiceMail`, {
      method: "POST",
      headers: hdrs,
      body: JSON.stringify({
        leadId: lead.id,
        type: "podziekowanie",
        company: lead.company,
      }),
    });
  };

  /* ══════════ EDIT HELPERS ══════════ */

  const fetchEditLead = async (id, field, value) => {
    await fetch(`${API}/editLead`, {
      method: "POST",
      headers: hdrs,
      body: JSON.stringify({
        id,
        field,
        value,
        userId: props.user.id,
        userName: props.user.name,
      }),
    }).catch(() => {});
    getData();
  };

  const editField = (field, currentValue) => {
    MySwal.fire({
      title: "Zmiana",
      input: "text",
      inputValue: currentValue || "",
      inputLabel: "nowa wartość",
      cancelButtonText: "Anuluj",
      showCancelButton: true,
      inputValidator: (value) => {
        fetchEditLead(lead.id, field, value);
      },
    });
  };

  const editStatus = () => {
    const model = {};
    statuses.forEach((s) => {
      model[s.name] = s.name;
    });
    MySwal.fire({
      title: "Wybierz status",
      input: "select",
      inputOptions: model,
      inputPlaceholder: "wybierz wartość",
      showCancelButton: true,
      cancelButtonText: "Anuluj",
      inputValidator: (v) =>
        new Promise((res) => res(v ? undefined : "musisz wybrać wartość")),
    }).then((result) => {
      if (result.isConfirmed) fetchEditLead(lead.id, "status", result.value);
    });
  };

  const deleteFile = async (id) => {
    await fetch(`${API}/deleteFile`, {
      method: "POST",
      headers: hdrs,
      body: JSON.stringify({ id }),
    }).catch(() => {});
    MySwal.fire({ icon: "success", title: "Plik", text: "usunięty" });
    getData();
  };

  const downloadFile = async (fileObj) => {
    try {
      const data = await fetch(fileObj.link);
      const blob = await data.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = fileObj.name;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error(err);
    }
  };

  const getUserName = (id) => {
    const u = users.find((u) => +u.id === +id);
    return u?.name || "—";
  };

  const sendSELLCRM = async (lead) => {
    await fetch(`${API}/sendLeadToSell`, {
      method: "POST",
      headers: hdrs,
      body: JSON.stringify({
        id: lead.id,
        userId: props.user.id,
        company: lead.company,
      }),
    }).catch(() => {});
    MySwal.fire({ icon: "success", title: "Przekazano", text: "do sprzedaży" });
    props.close();
  };

  /* ══════════ CHILD MODALS (pełnoekranowe) ══════════ */

  if (showNote) {
    return (
      <ShowNote
        close={() => setShowNote(false)}
        lead={lead}
        mode={noteMode}
        option={props.option}
      />
    );
  }
  if (showFileAdd) {
    return (
      <AddFile
        lead={props.lead}
        user={props.user}
        close={() => {
          setShowFileAdd(false);
          getData();
        }}
      />
    );
  }
  if (showTaskEdit && taskEdit) {
    return (
      <AddTask
        mode="edit"
        task={taskEdit}
        user={props.user}
        close={() => {
          setShowTaskEdit(false);
          getData();
        }}
      />
    );
  }
  if (showTaskAdd) {
    return (
      <AddTask
        mode="add"
        task=""
        user={props.user}
        close={() => {
          setShowTaskAdd(false);
          getData();
        }}
        lead={props.lead}
      />
    );
  }
  if (showContactEdit && contactEdit) {
    return (
      <AddContact
        mode="edit"
        contact={contactEdit}
        user={props.user}
        close={() => {
          setShowContactEdit(false);
          getData();
        }}
      />
    );
  }
  if (showContactAdd) {
    return (
      <AddContact
        mode="add"
        contact=""
        user={props.user}
        close={() => {
          setShowContactAdd(false);
          getData();
        }}
        lead={props.lead}
      />
    );
  }
  if (loading) return <Loading />;
  if (showFileAddOffer) {
    return (
      <AddFile
        lead={props.lead}
        user={props.user}
        type="oferta"
        close={() => {
          setShowFileAddOffer(false);
          getData();
        }}
      />
    );
  }

  /* ══════════ RENDER HELPERS ══════════ */

  const SOURCE_NOTE_LABELS = [
    { key: "bank", label: "Bank", company: "SKD,WIBOR,CHF" },
    { key: "ammount", label: "Kwota", company: "SKD,WIBOR,CHF" },
    { key: "creditDate", label: "Data kredytu", company: "SKD,WIBOR,CHF" },
    { key: "company", label: "Firmowy", company: "SKD,WIBOR,CHF" },
    { key: "change", label: "Przewalutowany", company: "CHF" },
    { key: "region", label: "Województwo", company: "SKD,WIBOR,CHF" },
  ];

  const formatAdditional = (raw) => {
    if (!raw) return null;
    try {
      const data = typeof raw === "string" ? JSON.parse(raw) : raw;
      return SOURCE_NOTE_LABELS.filter((f) =>
        f.company.split(",").includes(lead.company)
      )
        .filter((f) => data[f.key])
        .map((f) => (
          <span key={f.key} className="ld-sn-tag">
            <strong>{f.label}:</strong> {data[f.key]}
          </span>
        ));
    } catch {
      return raw;
    }
  };

  const TruncatedText = ({ text, maxLength = 120 }) => {
    const [expanded, setExpanded] = useState(false);
    if (!text) return "—";
    if (text.length <= maxLength) return text;
    return (
      <span>
        {expanded ? text : text.slice(0, maxLength) + "..."}
        <button
          className="ld-expand-btn"
          onClick={(e) => {
            e.stopPropagation();
            setExpanded((p) => !p);
          }}
        >
          {expanded ? "zwiń" : "rozwiń"}
        </button>
      </span>
    );
  };

  /* ══════════ RENDER ══════════ */

  return (
    <div className="ld-wrapper">
      {/* ── Inline AddTask (mid-flow) ── */}
      {showInlineTask && (
        <div className="ld-modal-overlay" style={{ zIndex: 200 }}>
          <AddTask
            mode="add"
            task=""
            user={props.user}
            lead={props.lead}
            prefill={pendingAfterTaskRef.prefill || {}}
            close={handleInlineTaskClose}
          />
        </div>
      )}

      {/* ── Header ── */}
      <div className="ld-header">
        <div className="ld-header-info">
          <div className="ld-avatar">
            {(lead?.name || "?")[0].toUpperCase()}
          </div>
          <div>
            <h2 className="ld-name">{lead?.name}</h2>
            <div className="ld-header-meta">
              {lead?.phone && <span>{lead.phone}</span>}
              {lead?.mail && <span>{lead.mail}</span>}
              {lead?.company && (
                <span className="ld-company-badge">{lead.company}</span>
              )}
            </div>
          </div>
        </div>
        <button className="ld-close" onClick={handleCloseCard} title="Zamknij">
          <X size={20} />
        </button>
      </div>

      {/* ── Tabs ── */}
      <div className="ld-tabs-bar">
        <div className="ld-tabs">
          {TABS.map((t) => {
            const Icon = t.icon;
            const count =
              t.id === 1
                ? tasks.length
                : t.id === 2
                ? contacts.length
                : t.id === 3
                ? files.length
                : t.id === 4
                ? logs.length
                : null;
            return (
              <button
                key={t.id}
                className={`ld-tab ${tab === t.id ? "ld-tab--active" : ""}`}
                onClick={() => setTab(t.id)}
              >
                <Icon size={15} />
                <span>{t.label}</span>
                {count !== null && count > 0 && (
                  <span className="ld-tab-count">{count}</span>
                )}
              </button>
            );
          })}
        </div>
        <div className="ld-quick-actions">
          {(props.option === "SPRZEDAZ" || lead.company === "SPRZEDAZ") && (
            <button
              className="lq-btn lq-btn--muted"
              onClick={() => setShowFileAddOffer(true)}
            >
              <Plus size={14} /> Oferta
            </button>
          )}
          {(isServiceDept ||
            !lead.status.toString().toLowerCase().includes("zainteresowany")) &&
            lead.company !== "SPRZEDAZ" && (
              <button
                className="lq-btn lq-btn--primary"
                onClick={handleServiceLead}
              >
                <User size={14} /> Obsługa leada
              </button>
            )}
          <button
            className="lq-btn lq-btn--muted"
            onClick={() => setShowTaskAdd(true)}
          >
            <Plus size={14} /> Zadanie
          </button>
          <button
            className="lq-btn lq-btn--muted"
            onClick={() => setShowContactAdd(true)}
          >
            <Plus size={14} /> Kontakt
          </button>
          <button
            className="lq-btn lq-btn--muted"
            onClick={() => setShowFileAdd(true)}
          >
            <Plus size={14} /> Plik
          </button>
        </div>
      </div>

      {/* ── Tab content ── */}
      <div className="ld-content">
        {/* TAB 0: Dane */}
        {tab === 0 && (
          <div className="ld-fields">
            <FieldRow label="ID" value={lead?.id} />
            <FieldRow
              label="Nazwa"
              value={lead?.name}
              onEdit={() => editField("name", lead?.name)}
              logField="name"
              leadId={lead?.id}
            />
            <FieldRow
              label="Telefon"
              value={lead?.phone}
              onEdit={() => editField("phone", lead?.phone)}
              logField="phone"
              leadId={lead?.id}
            />
            <FieldRow
              label="E-mail"
              value={lead?.mail}
              onEdit={() => editField("mail", lead?.mail)}
              logField="mail"
              leadId={lead?.id}
            />
            <FieldRow
              label="Źródło"
              value={lead?.source}
              logField="source"
              leadId={lead?.id}
            />
            <FieldRow
              label="Konsultant"
              value={props.seller || "brak danych"}
            />
            <FieldRow
              label="Data ostatniej rozmowy"
              value={lead?.lastTalkDate || "brak danych"}
            />
            <FieldRow label="Dział" value={lead?.company} />
            {props.option === "SPRZEDAZ" && (
              <FieldRow label="Dział produktu" value={lead?.subCompany} />
            )}
            <FieldRow label="Data pozyskania" value={lead?.date} />
            <FieldRow
              label="Informacje do rozmowy"
              value={
                lead?.sourceNote
                  ? ["SKD", "CHF", "WIBOR"].includes(
                      lead?.company === "SPRZEDAZ"
                        ? lead.subCompany
                        : props.option
                    )
                    ? "dane kredytu"
                    : "dane działki"
                  : "brak danych"
              }
              long
              onEdit={() => {
                setLeadDetail(lead);
                setNoteMode("source");
                setShowNoteB(true);
              }}
            />
            <FieldRow
              label="Dokument wygenerowany w zgłoszeniu"
              value={
                lead?.sourceLink ? (
                  <button
                    className="ld-link"
                    style={{
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                    }}
                    onClick={() => {
                      setViewerUrl(lead.sourceLink);
                      setViewerName("Źródło — " + lead.name);
                    }}
                  >
                    <Eye size={12} /> podgląd
                  </button>
                ) : (
                  "brak dokumentu"
                )
              }
            />
            <FieldRow
              label="Status"
              value={<span className="pg-status-badge">{lead?.status}</span>}
              onEdit={editStatus}
              logField="status"
              leadId={lead?.id}
            />
            <FieldRow
              label="Notatka wewnętrzna"
              value={<TruncatedText text={lead?.statusNote} maxLength={100} />}
              long
              onEdit={() => editField("statusNote", lead?.statusNote)}
              logField="statusNote"
              leadId={lead?.id}
            />
            <p
              style={{
                padding: "10px 20px",
                textAlign: "left",
                color: "#8c8ca1",
                textTransform: "uppercase",
                fontWeight: "600",
                fontSize: "14px",
                borderBottom: "1px solid var(--border, #e5e7eb)",
                backgroundColor: "#ffa50024",
              }}
            >
              Wysyłka do kancelarii / Dane dla sprzedaży
            </p>
            {!["SLUZEBNOSC", "SPRZEDAZ"].includes(lead.company) && (
              <FieldRow
                label="Informacje dla kancelarii"
                value={formatAdditional(lead?.validation)}
                long
                onEdit={() => setShowAdditionalLead(true)}
              />
            )}
            <FieldRow
              label="Godziny kontaktu dla kancelarii"
              value={lead?.hours}
              long
              onEdit={() => setShowAdditionalLead(true)}
            />
            <FieldRow
              label="Notatka dla kancelarii"
              value={<TruncatedText text={lead?.clientNote} maxLength={100} />}
              long
              onEdit={() => setShowAdditionalLead(true)}
            />
          </div>
        )}

        {/* TAB 1: Tasks */}
        {tab === 1 &&
          (tasks.length === 0 ? (
            <EmptyState text="Brak zadań" />
          ) : (
            <div className="lq-table-wrap db-table-scroll">
              <table className="lq-table">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Zadanie</th>
                    <th>Rodzaj</th>
                    <th>Data</th>
                    <th>Godzina</th>
                    <th>Status</th>
                    <th>Użytkownik</th>
                    <th>Akcja</th>
                  </tr>
                </thead>
                <tbody>
                  {tasks
                    .filter((r) =>
                      props.user.role === "user" ? +r.arch !== 1 : true
                    )
                    .sort((a, b) => new Date(b.date) - new Date(a.date))
                    .map((t) => (
                      <tr
                        key={t.id}
                        style={
                          t.type === "spotkanie"
                            ? { backgroundColor: "#ffa50024" }
                            : {}
                        }
                      >
                        <td>
                          <span
                            className={
                              t.status === "WYKONANE" ? "ld-done-badge" : ""
                            }
                          >
                            {t.id}
                          </span>
                        </td>
                        <td>{t.task}</td>
                        <td>{t?.type ?? "zwykłe"}</td>
                        <td className="lq-td-mono">{t.date}</td>
                        <td className="lq-td-mono">{t?.hour ?? "-"}</td>
                        <td>
                          <span
                            className={`ld-status ${
                              t.status === "WYKONANE"
                                ? "ld-status--done"
                                : "ld-status--pending"
                            }`}
                          >
                            {t.status}
                          </span>
                        </td>
                        <td>{getUserName(t.user)}</td>
                        <td>
                          <button
                            className="tc-action-btn tc-action-btn--view"
                            onClick={() => {
                              setTaskEdit(tasks.find((x) => +x.id === +t.id));
                              setShowTaskEdit(true);
                            }}
                          >
                            <Eye size={14} /> Zobacz
                          </button>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          ))}

        {/* TAB 2: Contacts */}
        {tab === 2 &&
          (contacts.length === 0 ? (
            <EmptyState text="Brak notatek kontaktowych" />
          ) : (
            <div className="lq-table-wrap db-table-scroll">
              <table className="lq-table">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Data</th>
                    <th>Treść</th>
                    <th>Użytkownik</th>
                    <th>Akcja</th>
                  </tr>
                </thead>
                <tbody>
                  {contacts
                    .sort((a, b) => new Date(b.date) - new Date(a.date))
                    .map((c) => (
                      <tr key={c.id}>
                        <td>{c.id}</td>
                        <td className="lq-td-mono">{c.date}</td>
                        <td>{c.content}</td>
                        <td>{getUserName(c.user)}</td>
                        <td>
                          <button
                            className="tc-action-btn tc-action-btn--view"
                            onClick={() => {
                              setContactEdit(
                                contacts.find((x) => +x.id === +c.id)
                              );
                              setShowContactEdit(true);
                            }}
                          >
                            <Eye size={14} /> Zobacz
                          </button>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          ))}

        {/* TAB 3: Files */}
        {tab === 3 && (
          <>
            {(props.option === "SPRZEDAZ" || lead.company === "SPRZEDAZ") && (
              <div className="ld-offer-section">
                <div className="ld-offer-header">
                  <span className="ld-offer-label">Oferta</span>
                  <button
                    className="lq-btn lq-btn--muted"
                    style={{ fontSize: 11, padding: "4px 10px" }}
                    onClick={() => setShowFileAddOffer(true)}
                  >
                    {oferta ? "Podmień" : "Dodaj"}
                  </button>
                </div>
                {oferta ? (
                  <div className="ld-offer-card">
                    <span className="ld-offer-name">{oferta.name}</span>
                    <div className="ld-file-actions">
                      <button
                        className="tc-action-btn tc-action-btn--view"
                        onClick={() => {
                          setViewerUrl(oferta.link);
                          setViewerName(oferta.name);
                        }}
                      >
                        <Eye size={14} /> Podgląd
                      </button>
                      <button
                        className="tc-action-btn tc-action-btn--view"
                        onClick={() => downloadFile(oferta)}
                      >
                        <Download size={14} /> Pobierz
                      </button>
                    </div>
                  </div>
                ) : (
                  <p className="ld-offer-empty">Brak oferty</p>
                )}
              </div>
            )}
            {regularFiles.length === 0 ? (
              <EmptyState text="Brak plików" />
            ) : (
              <div className="lq-table-wrap db-table-scroll">
                <table className="lq-table">
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Nazwa pliku</th>
                      <th>Użytkownik</th>
                      <th>Akcje</th>
                    </tr>
                  </thead>
                  <tbody>
                    {regularFiles
                      .sort((a, b) => new Date(b.date) - new Date(a.date))
                      .map((f) => (
                        <tr key={f.id}>
                          <td>{f.id}</td>
                          <td>{f.name}</td>
                          <td>{getUserName(f.user)}</td>
                          <td>
                            <div className="ld-file-actions">
                              <button
                                className="tc-action-btn tc-action-btn--view"
                                onClick={() => {
                                  setViewerUrl(f.link);
                                  setViewerName(f.name);
                                }}
                              >
                                <Eye size={14} /> Podgląd
                              </button>
                              <button
                                className="tc-action-btn tc-action-btn--view"
                                onClick={() => downloadFile(f)}
                              >
                                <Download size={14} /> Pobierz
                              </button>
                              <button
                                className="tc-action-btn tc-action-btn--accept"
                                style={{
                                  color: "#ef4444",
                                  borderColor: "#ef4444",
                                }}
                                onClick={() => deleteFile(f.id)}
                              >
                                <Trash2 size={14} /> Usuń
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}

        {/* TAB 4: Logs */}
        {tab === 4 &&
          (logs.length === 0 ? (
            <EmptyState text="Brak historii zmian" />
          ) : (
            <div className="ld-logs">
              {logs.map((log) => (
                <div key={log.id} className="ld-log">
                  <div className="ld-log-header">
                    <span className="ld-log-action">{log.action}</span>
                    <span className="ld-log-date">{log.date}</span>
                  </div>
                  {log.field && (
                    <div className="ld-log-detail">
                      <span className="ld-log-field">{log.field}</span>
                      {log.oldValue && (
                        <span className="ld-log-old">{log.oldValue}</span>
                      )}
                      {log.oldValue && log.newValue && (
                        <span className="ld-log-arrow">→</span>
                      )}
                      {log.newValue && (
                        <span className="ld-log-new">{log.newValue}</span>
                      )}
                    </div>
                  )}
                  <div className="ld-log-user">{log.userName || "system"}</div>
                </div>
              ))}
            </div>
          ))}

        {/* Overlay modals */}
        {showNoteB && (
          <ShowNote
            close={() => setShowNoteB(false)}
            lead={leadDetail}
            mode={noteMode}
            option={props.option}
          />
        )}
        {viewerUrl && (
          <FileViewer
            url={viewerUrl}
            fileName={viewerName}
            close={() => setViewerUrl(null)}
          />
        )}
        {showAdditionalLead && (
          <AdditionalLead
            mode="add"
            lead={lead}
            user={props.user}
            close={() => {
              setShowAdditionalLead(false);
              getData();
            }}
          />
        )}
        {showAdditional && (
          <AdditionalLead
            lead={lead}
            user={props.user}
            close={handleAdditionalClose}
          />
        )}
      </div>

      {props.user.sell === "1" && props.option !== "SPRZEDAZ" && (
        <div style={{ margin: "6px" }}>
          <div className="db-expand-field" style={{ width: "30%" }}>
            <button
              className="lq-btn lq-btn--dark"
              onClick={() => sendSELLCRM(lead)}
            >
              SPRZEDAZ
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

/* ── Helper components ── */

const FieldRow = ({ label, value, onEdit, long = false, logField, leadId }) => (
  <div className={`ld-field ${long ? "ld-field--long" : ""}`}>
    <div
      className="ld-field-label"
      style={{ textAlign: "left", width: "300px" }}
    >
      {label}
    </div>
    <div
      className="ld-field-value"
      style={{ textAlign: "left" }}
      {...(logField
        ? { "data-log-field": logField, "data-log-lead-id": leadId }
        : {})}
    >
      {value || "—"}
    </div>
    {onEdit && (
      <button className="db-icon-btn" onClick={onEdit}>
        {label === "Informacje do rozmowy" ? (
          <Eye size={20} />
        ) : (
          <Edit2 size={20} />
        )}
      </button>
    )}
  </div>
);

const EmptyState = ({ text }) => (
  <div className="ld-empty">
    <FileText size={32} strokeWidth={1} />
    <p>{text}</p>
  </div>
);

export default LeadDetail;
